import * as React from 'react';
import { AutoMode } from '../../images/AutoMode';
import BottleIcon from 'mdi-material-ui/BottleTonicPlus';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SettingsIcon from '@material-ui/icons/Settings';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AlertsSectionContainer } from '../../containers/Nav/AlertsSection';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { BackofficeSection } from './BackofficeSection';
import { ClientLinks } from './ClientLinks';
import { DeepReadonly } from 'utility-types';
import { DrugAndAlcoholMendixId } from '@pec/aion-ui-core/enums/MendixPageIds';
import { faHardHat } from '@fortawesome/free-solid-svg-icons/faHardHat';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { IOrganizationProfileStatus } from '@pec/aion-ui-core/interfaces/organizationProfileNotifications';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { Location } from 'history';
import { NavLinkListItem } from './NavLinkListItem';
import { QuestionnaireBuilderSection } from './QuestionnaireBuilderSection';
import { SafetyProgramsSection } from './SafetyProgramsSection';
import { SubscriberLinks } from './SubscriberLinks';
import { UserOrganizationSection } from './UserOrganizationSection';
import { UserOrganizationSelector } from './UserOrganizationSelector';
import { useTranslation } from 'react-i18next';
import NotificationsOutlined from '@material-ui/icons/NotificationsOutlined';
import { isComplianceRegionCHAS } from '@pec/aion-ui-core/helpers/complianceRegion';

const { Manage, Read } = ActivityAction;
const {
  Organizations,
  SafetyProgramEvaluations,
  SafetyPrograms,
  Users,
  Roles,
  PreQualificationGroups,
  QuestionnaireAdmin
} = ActivityResourceName;

type Props = {
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasVerificationSubscription: boolean;
  organization?: DeepReadonly<IOrganization> | null;
  promptForOrganizationLink: boolean;
  ssqId: string | null;
  sideNav?: JSX.Element;
  userOrganizations: DeepReadonly<IUserOrganization[]>;
  navExpanded: boolean;
  location: Location;
  organizationProfileNotifications?: IOrganizationProfileStatus;
};

export const DrawerLinksComponent: React.FC<Props> = ({
  organization,
  promptForOrganizationLink,
  ssqId,
  sideNav,
  userOrganizations,
  hasGlobalPermission,
  hasOrganizationPermission,
  hasVerificationSubscription,
  navExpanded,
  location,
  organizationProfileNotifications
}) => {
  const { t } = useTranslation();

  const isChasClient = isComplianceRegionCHAS(organization?.features || []);

  const isVisibleForNonChasOrganization =
    hasGlobalPermission(Manage, Organizations) ||
    !organization?.features.includes(OrganizationFeature.ChasOrganizationFields);

  return (
    <>
      {hasGlobalPermission(Manage, Organizations) && (
        <AnchorListItem divider tooltip={!navExpanded} href="/links" icon={<SettingsIcon />}>
          {t('components.drawerLinks.admin', 'Admin')}
        </AnchorListItem>
      )}
      {hasGlobalPermission(Read, QuestionnaireAdmin) && (
        <FeatureFlagWrapper>
          <Feature name="AionQuestionnaireBuilderUI">
            <QuestionnaireBuilderSection />
          </Feature>
        </FeatureFlagWrapper>
      )}
      <FeatureFlagWrapper>
        <Feature name="AionPreQualificationGroupsUI">
          {hasGlobalPermission(Read, PreQualificationGroups) &&
            (process.env.REACT_APP_NAME === AppName.AionPreQualificationGroupsUI &&
            location.pathname.startsWith('/supply-chains') ? (
              <NavLinkListItem divider tooltip={!navExpanded} to="/supply-chains" icon={<ListAltIcon />}>
                {t('components.drawerLinks.supplyChains', 'Supply Chains')}
              </NavLinkListItem>
            ) : (
              <AnchorListItem divider tooltip={!navExpanded} href="/supply-chains" icon={<ListAltIcon />}>
                {t('components.drawerLinks.supplyChains', 'Supply Chains')}
              </AnchorListItem>
            ))}
        </Feature>
      </FeatureFlagWrapper>
      {organization && ssqId ? (
        <FeatureFlagWrapper>
          <UserOrganizationSection
            hasGlobalPermission={hasGlobalPermission}
            hasOrganizationPermission={hasOrganizationPermission}
            organization={organization}
            promptForOrganizationLink={promptForOrganizationLink}
            ssqId={ssqId}
            organizationProfileNotifications={organizationProfileNotifications}
            isChasClient={isChasClient}
          />
        </FeatureFlagWrapper>
      ) : null}
      {(userOrganizations.length > 1 || userOrganizations[0]?.subsidiariesHierarchy?.children.length > 0) && (
        <UserOrganizationSelector userOrganizations={userOrganizations} organization={organization} />
      )}
      {organization &&
        !organization.features.includes(OrganizationFeature.DovetailCohortZero) &&
        isVisibleForNonChasOrganization &&
        !isChasClient && (
          <AlertsSectionContainer
            organizationId={organization.id}
            location={location}
            hasGlobalPermission={hasGlobalPermission}
            hasOrganizationPermission={hasOrganizationPermission}
          />
        )}
      {organization && organization.features.includes(OrganizationFeature.ChasNotifications) && (
        <AnchorListItem
          divider
          href={`/organizations/${organization.id}/notification-settings`}
          icon={<NotificationsOutlined />}
        >
          {t('components.drawerLinks.notificationManagement', 'Notifications')}
        </AnchorListItem>
      )}
      {hasGlobalPermission(Read, ActivityResourceName.ProcessAutomation) &&
        (process.env.REACT_APP_NAME === AppName.AionProcessAutomationUI ? (
          <NavLinkListItem divider tooltip={!navExpanded} to={`/process-automation`} icon={<AutoMode />}>
            {t('components.drawerLinks.processAutomation', 'Process Automation')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem divider tooltip={!navExpanded} href={`/process-automation`} icon={<AutoMode />}>
            {t('components.drawerLinks.processAutomation', 'Process Automation')}
          </AnchorListItem>
        ))}
      {sideNav}
      {(hasGlobalPermission(Manage, Organizations) ||
        hasGlobalPermission(Manage, Users) ||
        hasGlobalPermission(Manage, Roles)) && (
        <BackofficeSection hasGlobalPermission={hasGlobalPermission} location={location} />
      )}
      {organization &&
        organization.features.includes(OrganizationFeature.VerisourceDANavigation) &&
        (organization.features.includes(OrganizationFeature.Client) ? (
          <FeatureFlagWrapper>
            <Feature
              name={'HideLinkVeriforceCompany'}
              render={(isEnabled: boolean) => (
                <AnchorListItem
                  divider
                  tooltip={!navExpanded}
                  href={
                    isEnabled
                      ? undefined
                      : `/veriforce-integration/${organization.id}?RedirectUrl=/VS/start.aspx?mx=${DrugAndAlcoholMendixId.ClientIndex}`
                  }
                  icon={<BottleIcon />}
                >
                  {t('components.drawerLinks.drugAlcohol', 'Drug & Alcohol')}
                </AnchorListItem>
              )}
            />
          </FeatureFlagWrapper>
        ) : (
          <FeatureFlagWrapper>
            <Feature
              name={'HideLinkVeriforceCompany'}
              render={(isEnabled: boolean) => (
                <AnchorListItem
                  divider
                  tooltip={!navExpanded}
                  href={
                    isEnabled
                      ? undefined
                      : `/veriforce-integration/${organization.id}?RedirectUrl=/VS/start.aspx?mx=${DrugAndAlcoholMendixId.ContractorIndex}`
                  }
                  icon={<BottleIcon />}
                >
                  {t('components.drawerLinks.drugAlcohol', 'Drug & Alcohol')}
                </AnchorListItem>
              )}
            />
          </FeatureFlagWrapper>
        ))}
      {organization &&
        (organization.features.includes(OrganizationFeature.Client) ? (
          <ClientLinks
            organization={organization}
            hasOrganizationPermission={hasOrganizationPermission}
            location={location}
            isChasClient={isChasClient}
          />
        ) : (
          <SubscriberLinks
            navExpanded={navExpanded}
            organization={organization}
            hasGlobalPermission={hasGlobalPermission}
            hasOrganizationPermission={hasOrganizationPermission}
            hasVerificationSubscription={hasVerificationSubscription}
            location={location}
          />
        ))}
      {hasGlobalPermission(Manage, SafetyPrograms) ? (
        <SafetyProgramsSection />
      ) : (
        hasGlobalPermission(Manage, SafetyProgramEvaluations) &&
        (process.env.REACT_APP_NAME === AppName.AionSafetyProgramsUI ? (
          <NavLinkListItem
            divider
            tooltip={!navExpanded}
            to="/safety-program-requirements"
            icon={<FontAwesomeIcon icon={faHardHat} />}
          >
            {t('components.drawerLinks.programReviews', 'Program Reviews')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem
            divider
            tooltip={!navExpanded}
            href="/safety-program-requirements"
            icon={<FontAwesomeIcon icon={faHardHat} />}
          >
            {t('components.drawerLinks.programReviews', 'Program Reviews')}
          </AnchorListItem>
        ))
      )}
    </>
  );
};
